import React from "react";
import { colors2, UI_BODY_SM_DF } from "oolib";

/**
 * Numeric value + horizontal pill track with the 1.0 baseline marked at
 * center. A green/red fill grows OUTWARD from the baseline in the direction
 * of the value:
 *   value > 1 → fill grows right (green if above noiseFloor, grey near 1)
 *   value < 1 → fill grows left  (red if below 1/noiseFloor, grey near 1)
 *
 * Track is symmetric around the baseline: left half maps 0 → 1, right half
 * maps 1 → max. So 1.0 always sits dead center regardless of max.
 *
 * Designed for Lift / Strength readings where:
 *   1.0 = baseline (random chance)
 *   >1  = real association (pulls together)
 *   <1  = avoidance (pulls apart)
 *
 * `max` caps the right side of the scale — Strength values above max clip to
 * the right edge (extreme Lifts are usually small-sample artifacts).
 * `noiseFloor` controls the grey "around 1" band — values within
 * [1/noiseFloor, noiseFloor] render grey.
 */
const TableStrengthRamp = ({ value, max = 3, noiseFloor = 1.2 }) => {
  if (value === undefined || value === null) return null;
  const v = Number(value);
  if (Number.isNaN(v)) return null;

  const BASELINE_PCT = 50; // 1.0 always sits at the visual center.
  // Map value to a position on the track. Left half (0-50%) covers 0→1,
  // right half (50-100%) covers 1→max. Clamp out-of-range values to the edges.
  let valuePct;
  if (v >= 1) {
    const clamped = Math.min(v, max);
    valuePct = BASELINE_PCT + ((clamped - 1) / (max - 1)) * 50;
  } else {
    const clamped = Math.max(v, 0);
    valuePct = clamped * BASELINE_PCT; // v=0 → 0%, v=1 → 50%
  }

  const aboveBaseline = v >= 1;
  const fillLeft = aboveBaseline ? BASELINE_PCT : valuePct;
  const fillRight = aboveBaseline ? valuePct : BASELINE_PCT;

  const color =
    v >= noiseFloor
      ? colors2.green
      : v <= 1 / noiseFloor
      ? colors2.red
      : colors2.grey40;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        width: "100%",
        padding: "0.2rem 0",
        boxSizing: "border-box",
      }}
    >
      <UI_BODY_SM_DF style={{ color: colors2.grey80, minWidth: "2.4rem" }}>
        {v.toFixed(2)}
      </UI_BODY_SM_DF>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          // Same height as TableCountBar's bar (= BarChart barHeight: 24px),
          // so the two columns line up visually in shared rows.
          height: "24px",
          background: colors2.grey10,
          borderRadius: "999px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Value fill — rectangular, grows from baseline outward. The parent's
            overflow:hidden + borderRadius clips it into the pill shape at
            the edges. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: `${fillLeft}%`,
            width: `${fillRight - fillLeft}%`,
            top: 0,
            bottom: 0,
            background: color,
          }}
        />
        {/* Baseline marker — thin vertical line at center = 1.0. Rendered
            on top of the fill so the divider stays visible. */}
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: `${BASELINE_PCT}%`,
            top: 0,
            bottom: 0,
            width: "2px",
            background: colors2.grey90 || colors2.black,
            transform: "translateX(-50%)",
          }}
        />
      </div>
    </div>
  );
};

export default TableStrengthRamp;
